import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Edit2, Trash2, Plus, Save, X, Search } from 'lucide-react';
import Pagination from '../../Pagination';
import { useEscapeKey } from '../../hooks/useEscapeKey';
import { useToast } from '../../components/ui/Toast';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || '';

const AcceleratorsCRUD = ({ agents, categories, subcategories, onUpdate }) => {
  const [techStacks, setTechStacks] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingAccelerator, setEditingAccelerator] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');

  // Initialize toast and escape key hook
  const { toast } = useToast();
  useEscapeKey(() => setShowModal(false), showModal);

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    demo_url: '',
    category_id: '',
    subcategory_id: '',
    tech_category: '',
    input_requirements: '',
    ai_usage: '',
    output_provided: '',
    video_url: '',
    use_cases: [],
    customization_options: [],
    pricing_model: 'one_time',
    pricing_amount: 0,
    target_techstack: [],
    features: [],
    delivery_time: '',
    tags: [],
    tech_specs: {},
    benefits: [],
    is_active: true
  });

  // Get authentication headers
  const getAuthHeaders = () => {
    const token = localStorage.getItem('admin_token');
    return token ? { 'Authorization': `Bearer ${token}` } : {};
  };

  useEffect(() => {
    fetchTechStacks();
  }, []);

  const fetchTechStacks = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/techstack`);
      setTechStacks(response.data);
    } catch (error) {
      console.error('Error fetching tech stacks:', error);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      demo_url: '',
      category_id: '',
      subcategory_id: '',
      tech_category: '',
      input_requirements: '',
      ai_usage: '',
      output_provided: '',
      video_url: '',
      use_cases: [],
      customization_options: [],
      pricing_model: 'one_time',
      pricing_amount: 0,
      target_techstack: [],
      features: [],
      delivery_time: '',
      tags: [],
      tech_specs: {},
      benefits: [],
      is_active: true
    });
  };

  const handleAddNew = () => {
    setEditingAccelerator(null);
    resetForm();
    setShowModal(true);
  };

  const parseJSONField = (field) => {
    if (typeof field === 'string') {
      try {
        return JSON.parse(field);
      } catch (e) {
        return [];
      }
    }
    return Array.isArray(field) ? field : [];
  };

  const handleEdit = (accelerator) => {
    setEditingAccelerator(accelerator);
    setFormData({
      name: accelerator.name || '',
      description: accelerator.description || '',
      demo_url: accelerator.demo_url || '',
      category_id: accelerator.category_id || '',
      subcategory_id: accelerator.subcategory_id || '',
      tech_category: accelerator.tech_category || '',
      input_requirements: accelerator.input_requirements || '',
      ai_usage: accelerator.ai_usage || '',
      output_provided: accelerator.output_provided || '',
      video_url: accelerator.video_url || '',
      use_cases: parseJSONField(accelerator.use_cases) || [],
      customization_options: parseJSONField(accelerator.customization_options) || [],
      pricing_model: accelerator.pricing_model || 'one_time',
      pricing_amount: accelerator.pricing_amount || 0,
      target_techstack: parseJSONField(accelerator.target_techstack) || [],
      features: parseJSONField(accelerator.features) || [],
      delivery_time: accelerator.delivery_time || '',
      tags: parseJSONField(accelerator.tags) || [],
      tech_specs: accelerator.tech_specs || {},
      benefits: parseJSONField(accelerator.benefits) || [],
      is_active: accelerator.is_active !== false && accelerator.is_active !== 0
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const payload = {
        ...formData,
        demo_url: formData.demo_url || null,
        subcategory_id: formData.subcategory_id || null,
        input_requirements: formData.input_requirements || null,
        ai_usage: formData.ai_usage || null,
        output_provided: formData.output_provided || null,
        video_url: formData.video_url || null,
        delivery_time: formData.delivery_time || null,
        use_cases: JSON.stringify(formData.use_cases),
        customization_options: JSON.stringify(formData.customization_options),
        target_techstack: JSON.stringify(formData.target_techstack),
        features: JSON.stringify(formData.features),
        tags: JSON.stringify(formData.tags),
        tech_specs: JSON.stringify(formData.tech_specs),
        benefits: JSON.stringify(formData.benefits),
        is_active: formData.is_active
      };


      if (editingAccelerator) {
        // Update existing accelerator
        await axios.put(`${API_BASE_URL}/api/admin/accelerators/${editingAccelerator.id}`, payload, {
          headers: getAuthHeaders()
        });
        toast.success('Accelerator updated successfully!');
      } else {
        // Create new accelerator
        await axios.post(`${API_BASE_URL}/api/admin/accelerators`, payload, {
          headers: getAuthHeaders()
        });
        toast.success('Accelerator created successfully!');
      }

      setShowModal(false);
      setEditingAccelerator(null);
      resetForm();
      onUpdate(); // Refresh the data
    } catch (error) {
      console.error('Error saving accelerator:', error);
      toast.error(editingAccelerator ? 'Error updating accelerator.' : 'Error creating accelerator.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (acceleratorId) => {
    if (window.confirm('Are you sure you want to delete this accelerator?')) {
      try {
        await axios.delete(`${API_BASE_URL}/api/admin/accelerators/${acceleratorId}`, {
          headers: getAuthHeaders()
        });
        toast.success('Accelerator deleted successfully!');
        onUpdate(); // Refresh the data
      } catch (error) {
        console.error('Error deleting accelerator:', error);
        toast.error('Error deleting accelerator.');
      }
    }
  };

  // Array field management functions
  const addArrayItem = (field, item) => {
    if (item.trim()) {
      setFormData({
        ...formData,
        [field]: [...formData[field], item.trim()]
      });
    }
  };

  const removeArrayItem = (field, index) => {
    setFormData({
      ...formData,
      [field]: formData[field].filter((_, i) => i !== index)
    });
  };

  const handleTechStackChange = (techStackName, isChecked) => {
    if (isChecked) {
      setFormData({
        ...formData,
        target_techstack: [...formData.target_techstack, techStackName]
      });
    } else {
      setFormData({
        ...formData,
        target_techstack: formData.target_techstack.filter(name => name !== techStackName)
      });
    }
  };

  // Array input components
  const ArrayInput = ({ label, field, placeholder, value = [] }) => {
    const [inputValue, setInputValue] = useState('');

    const handleAdd = () => {
      if (inputValue.trim()) {
        addArrayItem(field, inputValue);
        setInputValue('');
      }
    };

    const handleKeyPress = (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        handleAdd();
      }
    };

    return (
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
        <div className="space-y-2">
          <div className="flex space-x-2">
            <input
              type="text"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyPress={handleKeyPress}
              className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
              placeholder={placeholder}
            />
            <button
              type="button"
              onClick={handleAdd}
              className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700"
            >
              Add
            </button>
          </div>
          <div className="flex flex-wrap gap-2">
            {value.map((item, index) => (
              <span
                key={index}
                className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-primary-100 text-primary-700"
              >
                {item}
                <button
                  type="button"
                  onClick={() => removeArrayItem(field, index)}
                  className="ml-2 text-primary-500 hover:text-primary-700"
                >
                  ×
                </button>
              </span>
            ))}
          </div>
        </div>
      </div>
    );
  };

  // Filter accelerators based on search term
  const filteredAccelerators = agents.filter(accelerator =>
    accelerator.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Pagination
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentAccelerators = filteredAccelerators.slice(indexOfFirstItem, indexOfLastItem);

  // Reset to first page when search term changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Accelerator Management</h1>
          <p className="text-gray-600">Manage your accelerators, their features, and configurations</p>
        </div>
        <button
          onClick={handleAddNew}
          className="bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700 transition-colors flex items-center space-x-2"
        >
          <Plus className="h-4 w-4" />
          <span>Add Accelerator</span>
        </button>
      </div>

      {/* Search Filter */}
      <div className="bg-white rounded-lg shadow-sm border p-4">
        <div className="flex items-center space-x-3">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search accelerators by title..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>
          {searchTerm && (
            <button
              onClick={() => setSearchTerm('')}
              className="px-3 py-2 text-sm text-gray-600 hover:text-gray-800 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Clear
            </button>
          )}
        </div>
        {searchTerm && (
          <div className="mt-2 text-sm text-gray-600">
            Showing {filteredAccelerators.length} of {agents.length} accelerators
          </div>
        )}
      </div>

      {/* Accelerators Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Accelerator
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Category
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Tech Category
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Tech Stack
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Pricing
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {currentAccelerators.map((accelerator) => (
              <tr key={accelerator.id} className="hover:bg-gray-50">
                <td className="px-6 py-4">
                  <div>
                    <div className="text-sm font-medium text-gray-900">
                      {accelerator.name}
                    </div>
                    <div className="text-sm text-gray-500 max-w-xs truncate">
                      {accelerator.description}
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-gray-900">
                    {accelerator.category_name}
                  </div>
                  <div className="text-sm text-gray-500">
                    {accelerator.subcategory_name}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-gray-900">
                    {accelerator.tech_category || 'Not set'}
                  </div>
                </td>
                <td className="px-6 py-4">
                  <div className="flex flex-wrap gap-1">
                    {parseJSONField(accelerator.target_techstack)?.slice(0, 2).map((tech, index) => (
                      <span key={index} className="inline-flex px-2 py-1 text-xs font-medium bg-green-100 text-green-700 rounded-full">
                        {tech}
                      </span>
                    ))}
                    {parseJSONField(accelerator.target_techstack)?.length > 2 && (
                      <span className="inline-flex px-2 py-1 text-xs font-medium bg-gray-100 text-gray-600 rounded-full">
                        +{parseJSONField(accelerator.target_techstack).length - 2}
                      </span>
                    )}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm font-medium text-green-600">
                    ${accelerator.pricing_amount}
                  </div>
                  <div className="text-sm text-gray-500">
                    {accelerator.pricing_model?.replace('_', ' ')}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                    accelerator.is_active !== false && accelerator.is_active !== 0
                      ? 'bg-green-100 text-green-800'
                      : 'bg-red-100 text-red-800'
                  }`}>
                    {accelerator.is_active !== false && accelerator.is_active !== 0 ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                  <button
                    onClick={() => handleEdit(accelerator)}
                    className="text-indigo-600 hover:text-indigo-900 mr-4"
                  >
                    <Edit2 className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => handleDelete(accelerator.id)}
                    className="text-red-600 hover:text-red-900"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {filteredAccelerators.length === 0 && (
          <div className="text-center py-12">
            <div className="text-gray-500 text-lg">
              {searchTerm ? 'No accelerators found matching your search' : 'No accelerators found'}
            </div>
            <p className="text-gray-400 mt-2">
              {searchTerm ? 'Try adjusting your search terms' : 'Add your first accelerator to get started'}
            </p>
          </div>
        )}
      </div>

      {/* Pagination */}
      {filteredAccelerators.length > 0 && (
        <Pagination
          currentPage={currentPage}
          totalItems={filteredAccelerators.length}
          itemsPerPage={itemsPerPage}
          onPageChange={setCurrentPage}
        />
      )}

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-6xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-lg font-medium text-gray-900">
                  {editingAccelerator ? 'Edit Accelerator' : 'Add New Accelerator'}
                </h3>
                <button
                  onClick={() => setShowModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>
              
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Status Toggle */}
                <div className="flex items-center justify-between bg-gray-50 border border-gray-200 rounded-lg px-4 py-3">
                  <div>
                    <p className="text-sm font-medium text-gray-900">Accelerator Status</p>
                    <p className="text-sm text-gray-500">
                      {formData.is_active
                        ? 'Visible on the public browse page and searchable by end users'
                        : 'Hidden from the public browse page and not searchable'}
                    </p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.is_active}
                      onChange={(e) => setFormData({...formData, is_active: e.target.checked})}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-primary-500 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
                    <span className="ml-3 text-sm font-medium text-gray-700">
                      {formData.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </label>
                </div>

                {/* Basic Information Section */}
                <div className="border-b border-gray-200 pb-6">
                  <h4 className="text-lg font-medium text-gray-900 mb-4">Basic Information</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Accelerator Name *
                      </label>
                      <input
                        type="text"
                        required
                        value={formData.name}
                        onChange={(e) => setFormData({...formData, name: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                        placeholder="e.g., React E-commerce Accelerator"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Delivery Time *
                      </label>
                      <input
                        type="text"
                        required
                        value={formData.delivery_time}
                        onChange={(e) => setFormData({...formData, delivery_time: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                        placeholder="e.g., 2-3 weeks"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Category *
                      </label>
                      <select
                        required
                        value={formData.category_id}
                        onChange={(e) => setFormData({...formData, category_id: e.target.value, subcategory_id: ''})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                      >
                        <option value="">Select Category</option>
                        {categories.map((category) => (
                          <option key={category.id} value={category.id}>
                            {category.name}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Subcategory
                      </label>
                      <select
                        value={formData.subcategory_id}
                        onChange={(e) => setFormData({...formData, subcategory_id: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                        disabled={!formData.category_id}
                      >
                        <option value="">Select Subcategory</option>
                        {subcategories
                          .filter(sub => sub.category_id === formData.category_id)
                          .map((subcategory) => (
                            <option key={subcategory.id} value={subcategory.id}>
                              {subcategory.name}
                            </option>
                          ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Tech Category
                      </label>
                      <input
                        type="text"
                        value={formData.tech_category}
                        onChange={(e) => setFormData({...formData, tech_category: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                        placeholder="e.g., AI/ML, Web Development, Data Analytics"
                      />
                      <p className="text-sm text-gray-500 mt-1">
                        Main technology category that will be displayed above the accelerator cards
                      </p>
                    </div>
                  </div>

                  <div className="mt-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Description *
                    </label>
                    <textarea
                      required
                      value={formData.description}
                      onChange={(e) => setFormData({...formData, description: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                      rows="3"
                      placeholder="Describe what this accelerator provides and its key benefits"
                    />
                  </div>

                  <div className="mt-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Demo URL
                    </label>
                    <input
                      type="url"
                      value={formData.demo_url}
                      onChange={(e) => setFormData({...formData, demo_url: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                      placeholder="https://example.com/demo"
                    />
                    <p className="text-sm text-gray-500 mt-1">
                      Optional: Link to a live demo of this accelerator
                    </p>
                  </div>
                </div>

                {/* Pricing Section */}
                <div className="border-b border-gray-200 pb-6">
                  <h4 className="text-lg font-medium text-gray-900 mb-4">Pricing</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Pricing Model *
                      </label>
                      <select
                        required
                        value={formData.pricing_model}
                        onChange={(e) => setFormData({...formData, pricing_model: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                      >
                        <option value="one_time">One Time</option>
                        <option value="monthly">Monthly</option>
                        <option value="yearly">Yearly</option>
                        <option value="per_project">Per Project</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Pricing Amount *
                      </label>
                      <input
                        type="number"
                        required
                        value={formData.pricing_amount}
                        onChange={(e) => setFormData({...formData, pricing_amount: parseFloat(e.target.value)})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                        min="0"
                        step="0.01"
                        placeholder="0.00"
                      />
                    </div>
                  </div>
                </div>

                {/* Technical Details Section */}
                <div className="border-b border-gray-200 pb-6">
                  <h4 className="text-lg font-medium text-gray-900 mb-4">Technical Details</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Technical Requirements
                      </label>
                      <textarea
                        value={formData.input_requirements}
                        onChange={(e) => setFormData({...formData, input_requirements: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                        rows="3"
                        placeholder="Prerequisites, system requirements, dependencies..."
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Implementation Approach
                      </label>
                      <textarea
                        value={formData.ai_usage}
                        onChange={(e) => setFormData({...formData, ai_usage: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                        rows="3"
                        placeholder="Technology stack, architecture, implementation methodology..."
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Deliverables & Outcomes
                      </label>
                      <textarea
                        value={formData.output_provided}
                        onChange={(e) => setFormData({...formData, output_provided: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                        rows="3"
                        placeholder="What the customer receives: code, documentation, training..."
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Demo Video URL
                      </label>
                      <input
                        type="url"
                        value={formData.video_url}
                        onChange={(e) => setFormData({...formData, video_url: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                        placeholder="https://youtube.com/embed/..."
                      />
                    </div>
                  </div>
                </div>

                {/* Compatible Tech Stacks Section */}
                <div className="border-b border-gray-200 pb-6">
                  <h4 className="text-lg font-medium text-gray-900 mb-4">Compatible Tech Stacks</h4>
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                    {techStacks.map((techStack) => (
                      <label key={techStack.id} className="flex items-center space-x-2 p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={formData.target_techstack.includes(techStack.name)}
                          onChange={(e) => handleTechStackChange(techStack.name, e.target.checked)}
                          className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                        />
                        <span className="text-lg">{techStack.icon}</span>
                        <span className="text-sm font-medium text-gray-700">{techStack.display_name}</span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Features & Benefits Section */}
                <div className="border-b border-gray-200 pb-6">
                  <h4 className="text-lg font-medium text-gray-900 mb-4">Features & Benefits</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <ArrayInput
                      label="Key Features"
                      field="features"
                      placeholder="Add a key feature..."
                      value={formData.features}
                    />
                    <ArrayInput
                      label="Benefits"
                      field="benefits"
                      placeholder="Add a benefit..."
                      value={formData.benefits}
                    />
                    <ArrayInput
                      label="Use Cases"
                      field="use_cases"
                      placeholder="Add a use case..."
                      value={formData.use_cases}
                    />
                    <ArrayInput
                      label="Customization Options"
                      field="customization_options"
                      placeholder="Add customization option..."
                      value={formData.customization_options}
                    />
                  </div>
                </div>

                {/* Tags Section */}
                <div className="pb-6">
                  <h4 className="text-lg font-medium text-gray-900 mb-4">Tags</h4>
                  <ArrayInput
                    label="Tags"
                    field="tags"
                    placeholder="Add a tag..."
                    value={formData.tags}
                  />
                </div>

                {/* Action Buttons */}
                <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
                  <button
                    type="button"
                    onClick={() => setShowModal(false)}
                    className="px-6 py-2 text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="px-6 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:opacity-50 flex items-center space-x-2"
                  >
                    {isSubmitting && <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>}
                    <span>{isSubmitting ? 'Saving...' : (editingAccelerator ? 'Update Accelerator' : 'Create Accelerator')}</span>
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AcceleratorsCRUD;